import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import useError from "src/hooks/useError";
import { authSelector } from "src/store/reducers/auth";
import { useWishlistSyncMutation } from "src/store/api/accounts/accountsApiSlice";
import {
    clearGuestWishlist,
    getGuestWishlist,
    setGuestWishlistSyncState,
} from "src/utils/guestWishlist";

const useGuestWishlistSync = () => {
    const auth = useSelector(authSelector);
    const previousAccessRef = useRef(auth.access);
    const [syncWishlist] = useWishlistSyncMutation();
    const { showError } = useError();

    useEffect(() => {
        const hadAccess = Boolean(previousAccessRef.current);
        const hasAccess = Boolean(auth.access);

        previousAccessRef.current = auth.access;

        if (hadAccess || !hasAccess) {
            return;
        }

        const guestWishlistSlugs = getGuestWishlist();

        if (!guestWishlistSlugs.length) {
            return;
        }

        let isMounted = true;

        const syncHandle = async () => {
            setGuestWishlistSyncState("syncing");

            try {
                await syncWishlist({ slugs: guestWishlistSlugs }).unwrap();

                if (!isMounted) {
                    return;
                }

                clearGuestWishlist();
            } catch (error) {
                if (isMounted) {
                    showError(error);
                }
            } finally {
                if (isMounted) {
                    setGuestWishlistSyncState("idle");
                }
            }
        };

        syncHandle();

        return () => {
            isMounted = false;
            setGuestWishlistSyncState("idle");
        };
    }, [auth.access, showError, syncWishlist]);
};

export default useGuestWishlistSync;
